import { SchemaFormat } from '../interfaces/nft.interface';

export interface AsciiRun {
  start: number;
  end: number;
  text: string;
}

export function extractAsciiRuns(bytes: ArrayLike<number>, minLen = 3): AsciiRun[] {
  const runs: AsciiRun[] = [];
  let start: number | null = null;

  const isPrintable = (b: number) => b >= 32 && b <= 126;

  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i] & 0xff;
    if (isPrintable(b)) {
      if (start === null) start = i;
    } else if (start !== null) {
      const len = i - start;
      if (len >= minLen) {
        const text = String.fromCharCode(
          ...Array.from({ length: len }, (_, k) => bytes[start ? start : 0 + k] & 0xff)
        );
        runs.push({ start, end: i - 1, text });
      }
      start = null;
    }
  }
  if (start !== null) {
    const len = bytes.length - start;
    if (len >= minLen) {
      const text = String.fromCharCode(
        ...Array.from({ length: len }, (_, k) => bytes[start ? start : 0 + k] & 0xff)
      );
      runs.push({ start, end: bytes.length - 1, text });
    }
  }
  return runs;
}

export function ipfsToHttp(
  value: string | undefined | null,
  gateway = 'https://ipfs.io/ipfs/'
): string | null {
  if (!value) return null;
  const v = value.trim();

  // schon eine http(s)-URL?
  if (/^https?:\/\//i.test(v)) return v;

  // ipfs://CID[/path]
  if (/^ipfs:\/\//i.test(v)) return v.replace(/^ipfs:\/\//i, gateway);

  // „nackte“ CIDs (CIDv0 beginnt oft mit Qm..., CIDv1 oft mit baf...)
  if (/^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[1-9A-HJ-NP-Za-km-z]{20,})/.test(v)) {
    return gateway + v;
  }

  return null;
}

export function readVaruint(bytes: Uint8Array, off: number): { value: number; size: number } {
  let value = 0,
    shift = 0,
    pos = off;
  // LEB128
  while (true) {
    const b = bytes[pos++];
    value |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) break;
    shift += 7;
  }
  return { value, size: pos - off };
}

export function readString(bytes: Uint8Array, off: number) {
  const { value: len, size } = readVaruint(bytes, off);
  const start = off + size;
  const end = start + len;
  const text = new TextDecoder().decode(bytes.slice(start, end));
  return { value: text, size: size + len };
}

/** Deserialisiert serialized_data anhand des Schema-Formats (unterstützt: uint16, string) */
export function deserializeAtomicAssetsData(
  format: SchemaFormat,
  raw: number[]
): Record<string, string | number> {
  const bytes = new Uint8Array(raw || []);
  let off = 0;
  const out: Record<string, string | number> = {};
  while (off < bytes.length) {
    // 1) Feld-Identifier (Varuint): id = schemaIndex + 4
    const { value: ident, size: idSize } = readVaruint(bytes, off);
    off += idSize;
    const index = ident - 4;
    const f = format[index];
    if (!f) break; // unbekanntes Feld oder kaputter Stream

    // 2) typabhängig lesen
    switch (f.type) {
      case 'uint16': {
        const { value, size } = readVaruint(bytes, off);
        out[f.name] = value;
        off += size;
        break;
      }
      case 'string': {
        const { value, size } = readString(bytes, off);
        out[f.name] = value;
        off += size;
        break;
      }
      default:
        // falls im Schema später andere Typen auftauchen: hier erweitern
        // zur Sicherheit „überspringen“ nicht implementiert – wir brechen ab.
        console.warn('Nicht unterstützter Typ im Deserializer:', f.type);
        return out;
    }
  }
  return out;
}
